import React, { Fragment, useState } from 'react';
import PropTypes from 'prop-types';
import { get, size } from 'lodash/fp';
import { useSelector, useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { Button, ButtonToolbar, Icon } from 'rsuite';
import { COMPLETED } from '../../../../../constants/pos/OrderStatus';
import CancelConfirmation from '../../../../../components/POS/CancelConfirmationModal';
import CancelOrderConfirmation from '../../../../../components/POS/CancelOrderConfirmationModal';
import CancelChangesConfirmation from '../../../../../components/POS/CancelChangesConfirmationModal';
import SaveChangesConfirmation from '../../../../../components/POS/SaveChangesConfirmationModal';
import {
  getIsSaving,
  getIsSavingItem,
  getIsDeletingItem,
  getOrderForVisitTrackId
} from '../../../../../redux/POS/selectors/order';
import { parseInt } from '../../../../../helpers/utils';
import {
  completePosOrder,
  cancelPosOrder,
  adjustPosOrder,
  deletePosOrderItems
} from '../../../../../redux/sagas/routines';
import {
  hasOrderAdjustment,
  getPosOrderAdjustment,
  getOrderItemsWithAdjustment
} from '../../../../../redux/POS/selectors/orderAdjustment';
import {
  clearOrderAdjustment,
  clearPosOderSavingError
} from '../../../../../redux/POS/actions';

import redirectToVisitPage from '../../../../../helpers/pos/redirectToVisitPage';

const CancelButton = ({ label, icon, ...props }) => (
  <Button
    color="orange"
    appearance="ghost"
    style={{ backgroundColor: 'white' }}
    {...props}
  >
    <Icon icon={icon} /> {label}
  </Button>
);

CancelButton.propTypes = {
  label: PropTypes.string.isRequired,
  icon: PropTypes.string
};

CancelButton.defaultProps = {
  icon: 'close-circle'
};

const SaveButton = ({ label, ...props }) => (
  <Button
    color="green"
    appearance="ghost"
    style={{ backgroundColor: 'white' }}
    {...props}
  >
    <Icon icon="check-circle" /> {label}
  </Button>
);

SaveButton.propTypes = {
  label: PropTypes.string.isRequired
};

function ActionButtons({ ...props }) {
  const dispatch = useDispatch();
  const visitTrackId = parseInt(get('visitTrackId', useParams()));

  const [showCancelConfirmation, setShowCancelConfirmation] = useState(false);
  const [
    showCancelOrderConfirmation,
    setShowCancelOrderConfirmation
  ] = useState(false);
  const [
    showCancelChangesConfirmation,
    setShowCancelChangesConfirmation
  ] = useState(false);
  const [
    showSaveChangesConfirmation,
    setShowSaveChangesConfirmation
  ] = useState(false);
  const [isReturnItems, setIsReturnItems] = useState(null);
  const order = useSelector(state =>
    getOrderForVisitTrackId(state, visitTrackId)
  );
  const isSaving = useSelector(getIsSaving);
  const isSavingItem = useSelector(getIsSavingItem);
  const isDeletingItem = useSelector(getIsDeletingItem);
  const hasAdjustment = useSelector(hasOrderAdjustment);
  const orderAdjustment = useSelector(getPosOrderAdjustment);
  const status = parseInt(get('status', order));
  const orderItems = get('items', order);
  const hasItems = size(orderItems) > 0;
  const orderItemsWithAdjustment = useSelector(state =>
    getOrderItemsWithAdjustment(state, orderItems)
  );
  const isOrderCompleted = status === COMPLETED;

  const hasItemsOnCart = size(orderItemsWithAdjustment) > 0;
  const isOrderCompletedAndNoChanges = isOrderCompleted && !hasAdjustment;
  const isOrderCompletedAndHasChanges = isOrderCompleted && hasAdjustment;
  const isOrderCompletedAndHasItems = isOrderCompleted && hasItems;
  const isOrderIncompleteAndHasItems = !isOrderCompleted && hasItems;

  const toggleCancelConfirmation = () => {
    const showModal = !showCancelConfirmation;
    setShowCancelConfirmation(showModal);
  };

  const toggleCancelOrderConfirmation = () => {
    const showModal = !showCancelOrderConfirmation;
    setShowCancelOrderConfirmation(showModal);
  };

  const toggleCancelChangesConfirmation = () => {
    const showModal = !showCancelChangesConfirmation;
    setShowCancelChangesConfirmation(showModal);
  };

  const toggleSaveChangesConfirmation = () => {
    const showModal = !showSaveChangesConfirmation;
    setShowSaveChangesConfirmation(showModal);
  };

  const handleOnBack = () => {
    if (isOrderIncompleteAndHasItems) {
      toggleCancelConfirmation();
    } else {
      redirectToVisitPage(order.clientId);
    }
  };

  const handleChangeCancelOrderOption = value => {
    setIsReturnItems(value);
  };

  const handleOnCompleteOrder = () => {
    dispatch(completePosOrder(order.id));
  };

  const handleOnConfirmCancel = () => {
    dispatch(deletePosOrderItems(order));
    toggleCancelConfirmation();
  };

  const handleOnConfirmCancelChanges = () => {
    dispatch(clearOrderAdjustment(order.id));
    dispatch(clearPosOderSavingError());
    toggleCancelChangesConfirmation();
  };

  const handleOnConfirmCancelOrder = () => {
    const { id } = order;
    dispatch(cancelPosOrder({ id, isReturnItems }));
    toggleCancelOrderConfirmation();
  };

  const handleOnCloseCancelOrder = () => {
    setIsReturnItems(null);
    toggleCancelOrderConfirmation();
  };

  const handleOnConfirmSaveChanges = () => {
    const { id } = order;
    dispatch(adjustPosOrder({ id, orderAdjustment }));
    toggleSaveChangesConfirmation();
  };

  return (
    <Fragment>
      <ButtonToolbar {...props} className="link-group">
        {((isOrderCompletedAndHasItems && !hasAdjustment) ||
          !isOrderCompleted) && (
          <CancelButton
            disabled={!order || isSaving || isSavingItem || isDeletingItem}
            icon="chevron-circle-left"
            onClick={handleOnBack}
            label={isOrderIncompleteAndHasItems ? 'Cancel' : 'Back'}
          />
        )}

        {isOrderCompletedAndNoChanges && (
          <CancelButton
            disabled={isSaving}
            onClick={toggleCancelOrderConfirmation}
            label="Cancel Order"
          />
        )}

        {isOrderCompletedAndHasChanges && (
          <CancelButton
            disabled={isSaving}
            onClick={toggleCancelChangesConfirmation}
            label="Cancel Changes"
          />
        )}

        {!isOrderCompleted && (
          <SaveButton
            disabled={!hasItems || isSaving || isSavingItem || isDeletingItem}
            onClick={handleOnCompleteOrder}
            label="Save Order"
          />
        )}

        {isOrderCompletedAndHasChanges && (
          <SaveButton
            disabled={isSaving || !hasItemsOnCart}
            onClick={toggleSaveChangesConfirmation}
            label="Save"
          />
        )}
      </ButtonToolbar>
      {order && (
        <CancelConfirmation
          show={showCancelConfirmation}
          onCancel={toggleCancelConfirmation}
          onConfirm={handleOnConfirmCancel}
          order={order}
        />
      )}
      {order && (
        <CancelOrderConfirmation
          show={showCancelOrderConfirmation}
          onCancel={handleOnCloseCancelOrder}
          onConfirm={handleOnConfirmCancelOrder}
          order={order}
          onSelectOption={handleChangeCancelOrderOption}
          confirmButtonProps={{ disabled: isReturnItems === null }}
        />
      )}
      {order && (
        <CancelChangesConfirmation
          show={showCancelChangesConfirmation}
          onCancel={toggleCancelChangesConfirmation}
          onConfirm={handleOnConfirmCancelChanges}
          order={order}
        />
      )}
      {order && (
        <SaveChangesConfirmation
          show={showSaveChangesConfirmation}
          onCancel={toggleSaveChangesConfirmation}
          onConfirm={handleOnConfirmSaveChanges}
          order={order}
        />
      )}
    </Fragment>
  );
}

export default ActionButtons;
